import type { ABTest } from '@lmn/lmn-commonlogic/dist/config/abtest/ABTestsTypes';
import type { ConfigValues } from '@/app/common/config/configuration/configurationTypes.ts';

/**
 * Resolves the names listed in the `debug_values` feature toggle to what the page actually got.
 *
 * The toggle carries a comma-separated list of A/B experiment names and/or config values, and the
 * debug panel prints each one beside its resolved value — so a session that is behaving oddly can
 * be read off the screen instead of inferred from logs. A/B tests win over config on a name clash,
 * because experiment names (`cux_*`) never collide with config names in practice and the A/B lookup
 * is the exact one the site itself did.
 *
 * Deliberately free of `CONFIGURATION_PARAMS`: that module pulls the memory-cache getter with it,
 * which must not reach the client bundle (same reasoning as `evoLayout.ts`). The cacheName →
 * param-name mapping is therefore done by converting snake_case to camelCase rather than by
 * consulting the table, so a name whose cacheName is not the snake_case of its key has to be listed
 * by its camelCase key instead.
 */

/**
 * Reserved name. It is neither an experiment nor a config value: listing it prints the visitor's
 * Casper user id, and leaving it out keeps the id off the panel even while other names are listed.
 * Matched exactly, so a near miss like `userid` falls through to the normal lookup and prints
 * `not found` — visible feedback rather than a line that silently never appears.
 */
export const DEBUG_VALUE_USER_ID = 'userId';

export type DebugValueSource = 'A/B' | 'config' | 'unknown';

export type DebugValue = {
  name: string;
  source: DebugValueSource;
  value: string;
};

/** `apex_calendar_enabled` -> `apexCalendarEnabled`. */
const toCamelCase = (name: string): string => name.replace(/_([a-z0-9])/g, (_, char: string) => char.toUpperCase());

export const parseDebugValueNames = (raw: string | undefined): string[] =>
  (raw ?? '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);

export const isDebugValuesEnabled = (raw: string | undefined): boolean => parseDebugValueNames(raw).length > 0;

/** Whether the list asks for the user id. */
export const hasDebugUserId = (raw: string | undefined): boolean =>
  parseDebugValueNames(raw).includes(DEBUG_VALUE_USER_ID);

/**
 * A null version is not the same as a missing experiment: the visitor may be outside the perimeter,
 * or in it with no variant assigned (a SAAB failure). Those are different bugs, so they print
 * differently.
 */
const formatABTest = (abTest: ABTest): string => {
  if (abTest.version) return abTest.version;
  return abTest.isInPerimeter ? 'in perimeter, no variant' : 'out of perimeter';
};

const readConfigValue = (configValues: ConfigValues | undefined, name: string): boolean | string | undefined => {
  if (!configValues) return undefined;
  const entry = configValues[name as keyof ConfigValues] ?? configValues[toCamelCase(name) as keyof ConfigValues];
  return entry?.value;
};

export const resolveDebugValues = ({
  names,
  abTests,
  configValues,
}: {
  names: string[];
  abTests: ABTest[] | undefined;
  configValues: ConfigValues | undefined;
}): DebugValue[] =>
  // The user id is rendered as its own panel line, so it must not also come back here as an
  // unresolvable name.
  names
    .filter((name) => name !== DEBUG_VALUE_USER_ID)
    .map((name) => {
      const abTest = abTests?.find((test) => test.experimentName === name);
      if (abTest) return { name, source: 'A/B', value: formatABTest(abTest) };

      const configValue = readConfigValue(configValues, name);
      if (configValue !== undefined) return { name, source: 'config', value: String(configValue) };

      return { name, source: 'unknown', value: 'not found' };
    });
